import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useEntitlements } from "../src/components/DataContext/EntitlementsContext";
import { useMsal } from "@azure/msal-react";
import { tabsConfig } from "./components/AssetDashboard/tabsConfig";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { slugFromPath } from "./shellHost/host";

interface Entitlement {
  po_path: string;
}

const staticValidPaths = [
  "/landing",
  "/filecenter",
  "/administration",
  "/knowledge-center",
  "/metric-dictionary",
  "/custom-reports",
  "/market-fundamentals",
  "/management",
  "/outages",
  "/outage-management",
  "/Revenue-Tab"
];

const dynamicTabPaths = tabsConfig.tabs.map(tab => 
  `/asset-dashboard/${tab.label.toLowerCase().replace(/\s+/g, "-")}`
);

const validPaths = [...staticValidPaths, ...dynamicTabPaths];

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const location = useLocation();
  const { entitlements: contextEntitlements } = useEntitlements();
  const [cachedEntitlements, setCachedEntitlements] = useState<Entitlement[]>([]);
  const [authReady, setAuthReady] = useState(false);

  const pathname = location.pathname.toLowerCase().trim();

  // Auth check
  useEffect(() => {
    const checkAuth = async () => {
      let account = instance.getActiveAccount() || accounts[0];

      if (!account && accounts.length > 0) {
        account = accounts[0];
        instance.setActiveAccount(account);
      }

      if (!account) {
        try {
          await instance.ssoSilent({
            scopes: ["User.Read"],
            loginHint: accounts[0]?.username,
          });
          setAuthReady(true);
        } catch (err) {
          if (err instanceof InteractionRequiredAuthError) {
            // instance.loginRedirect({
            //   scopes: ["User.Read", "Group.Read.All"],
            //   redirectStartPage: window.location.href,
            // });
          } else {
            console.error("Unexpected MSAL auth error:", err);
          }
        }
      } else {
        setAuthReady(true);
      }
    };

    checkAuth();
  }, [instance, accounts]);

  useEffect(() => {
    if (contextEntitlements.length > 0) {
      localStorage.setItem("entitlements", JSON.stringify(contextEntitlements));
      setCachedEntitlements(contextEntitlements);
    } else {
      const stored = localStorage.getItem("entitlements");
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) {
            setCachedEntitlements(parsed);
          }
        } catch {
          console.warn("Invalid entitlements cache");
        }
      }
    }
  }, [contextEntitlements]);
 
  const entitlements = cachedEntitlements;

 
  const getTopLevelPath = (path: string) => {
    const parts = path.toLowerCase().split("/").filter(Boolean);

    if (parts.length === 0) {
      return "/";
    }

    if (parts[0] === "asset-dashboard") {
      return `/${parts.slice(0, 2).join("/")}`;
    }

    return `/${parts[0]}`;
  };
  
  const topLevelPath = getTopLevelPath(pathname);
  // /apps/<slug> is a child app. Whether the user may open it is decided by the
  // child-app list (ChildAppMount sends everyone else to /unauthorized), not by
  // AMP's own po_path entitlements, so it skips the path checks below.
  const isChildAppPath = slugFromPath(pathname) !== null;
  // The Knowledge Center is open to every signed-in user.
  const isOpenToAll = ["/knowledge-center", "/metric-dictionary"].includes(topLevelPath);

  const isValidPath = isChildAppPath || validPaths.includes(topLevelPath);
  const hasAccess =
    isChildAppPath ||
    isOpenToAll ||
    entitlements.some((e) => topLevelPath === e.po_path?.toLowerCase().trim());
  

 
if (!authReady || entitlements.length === 0) {
  const account = instance.getActiveAccount() || accounts[0];
  if (!account) {
    // Save the intended destination so SignIn can redirect back after login
    sessionStorage.setItem("postLoginRedirect", location.pathname + location.search);
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return null;
}


  if (!isValidPath) {
    return <Navigate to="/not-found" state={{ from: location }} replace />;
  }

  if (!hasAccess) {
    return <Navigate to="/unauthorized" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
